<template>
  <section
    class="relative min-h-[75vh] flex items-center justify-center text-center p-6 lg:py-24 overflow-hidden lg:min-h-[100vh] 2xl:min-h-[80vh]"
  >
    <div
      v-if="heroImageUrl"
      class="absolute inset-0 bg-cover bg-center sm:bg-center md:bg-[center_35%] lg:bg-center bg-no-repeat will-change-transform"
      :style="{
        backgroundImage: `url('${heroImageUrl}')`,
        transform: `translate3d(0, ${parallaxOffset}px, 0)`,
      }"
    ></div>

    <div v-if="heroImageUrl" class="absolute inset-0 bg-black/50"></div>

    <div
      class="absolute inset-0 bg-gradient-to-b from-bochas-bg/80 via-transparent to-bochas-bg"
    ></div>

    <!-- Redes: horizontales al pie en mobile, verticales al costado en desktop -->
    <div
      v-if="socials.length"
      class="absolute bottom-10 left-1/2 z-20 flex -translate-x-1/2 flex-row gap-4 lg:bottom-auto lg:left-auto lg:right-6 lg:top-1/2 lg:-translate-x-0 lg:-translate-y-1/2 lg:flex-col"
      aria-label="Redes sociales"
    >
      <a
        v-for="social in socials"
        :key="social.name"
        :href="social.href"
        target="_blank"
        rel="noopener"
        :aria-label="social.label"
        class="social-link flex h-10 w-10 items-center justify-center rounded-full border border-white/50 bg-white/10 text-white backdrop-blur hover:border-white/80 hover:bg-white/20"
      >
        <IconFbochas :name="social.icon" :size="18" />
      </a>
    </div>

    <div class="relative z-10 max-w-7xl">
      <h1
        class="hero-copy hero-copy--title text-4xl md:text-6xl font-bold text-white mb-4 leading-tight"
      >
        {{ activeTitle }}
      </h1>
      <p
        class="hero-copy hero-copy--subtitle text-2xl font-semibold text-white md:text-4xl md:font-medium"
      >
        {{ activeSubtitle }}
        ❤️
      </p>

      <div
        class="mt-10 grid w-full grid-cols-2 items-center justify-center gap-3 sm:gap-4 lg:mt-24 lg:flex lg:flex-wrap lg:gap-6"
      >
        <a
          v-for="(cta, idx) in data.ctas"
          :key="cta.label"
          :href="cta.href"
          :style="{ '--cta-delay': `${180 + idx * 110}ms` }"
          :class="[
            'hero-cta inline-flex min-w-0 items-center justify-center rounded-3xl px-3 py-3 text-sm font-normal text-white sm:px-5 sm:text-base lg:px-6 lg:text-lg',
            idx === 2 ? 'col-span-2 justify-self-center' : 'w-full lg:w-auto',
            idx === 0 ? 'btn-bochas-rojo' : '',
            idx === 1 ? 'btn-bochas-azul' : '',
            idx === 2 ? 'btn-bochas-trans' : '',
          ]"
        >
          <span class="hero-cta__label">{{ cta.label }}</span>
          <span class="hero-cta__arrow" aria-hidden="true">→</span>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import IconFbochas from "../Icons/IconFbochas.vue";
import { getHeroData } from "@/services/landing/landingService";
import { obtenerSiteMediaMap } from "@/services/siteMedia/siteMediaService";
import { obtenerSiteSettingsMap } from "@/services/siteSettings/siteSettingsService";
import { useScrollY } from "@/composables/useScrollY";
import { obtenerSiteVisibility } from "@/services/siteSettings/siteVisibilityService";
import { HOME_HERO_FALLBACK_SLIDES } from "@/data/siteMediaFallbacks";

const data = ref({ title: "", hashtag: "", ctas: [] });
const slides = ref([...HOME_HERO_FALLBACK_SLIDES]);
const activeSlideIndex = ref(0);
let slideTimer = null;

const socialOptions = [
  { name: "whatsapp", label: "WhatsApp", icon: "whatsapp" },
  { name: "instagram", label: "Instagram", icon: "instagram" },
  { name: "facebook", label: "Facebook", icon: "facebook" },
];
const socials = ref([]);

// Parallax: la imagen de fondo se mueve al 40% de la velocidad del scroll
const scrollY = useScrollY();
const parallaxOffset = computed(() => scrollY.value * 0.4);
const activeSlide = computed(() => slides.value[activeSlideIndex.value] || null);
const heroImageUrl = computed(() => activeSlide.value?.imageUrl || "");
const activeTitle = computed(() => activeSlide.value?.title || data.value.title);
const activeSubtitle = computed(
  () => activeSlide.value?.subtitle || data.value.hashtag,
);

function startSlideTimer() {
  if (slideTimer || slides.value.length <= 1) return;
  slideTimer = window.setInterval(() => {
    activeSlideIndex.value = (activeSlideIndex.value + 1) % slides.value.length;
  }, 5500);
}

onMounted(async () => {
  const fallbackData = await getHeroData();
  data.value = fallbackData;
  startSlideTimer();
  try {
    const [media, settings, visibility] = await Promise.all([
      obtenerSiteMediaMap(),
      obtenerSiteSettingsMap(),
      obtenerSiteVisibility(),
    ]);
    const hero = media["home.hero"];
    const heroSlides = Object.values(media)
      .filter((item) => /^home\.hero\.\d+$/.test(item.key) && item.imageUrl)
      .sort(
        (a, b) =>
          Number(a.key.split(".").pop()) - Number(b.key.split(".").pop()),
      );
    activeSlideIndex.value = 0;
    slides.value = heroSlides.length
      ? heroSlides
      : hero?.imageUrl
        ? [
          {
            imageUrl: hero.imageUrl,
            title: hero?.title || fallbackData.title,
            subtitle: hero?.subtitle || fallbackData.hashtag,
          },
        ]
        : [];
    data.value = {
      ...fallbackData,
      title: hero?.title || fallbackData.title,
      hashtag: hero?.subtitle || fallbackData.hashtag,
      ctas: fallbackData.ctas.filter((cta) => {
        if (cta.href === "#próximas-fechas") return visibility["pages.calendario"] !== false;
        if (cta.href.startsWith("/la-federacion")) return visibility["pages.federacion"] !== false;
        return true;
      }),
    };
    startSlideTimer();
    socials.value = socialOptions
      .map((social) => ({
        ...social,
        href: String(settings[`social.${social.name}`] || "").trim(),
      }))
      .filter((social) => social.href && social.href !== "#");
  } catch (_) {
    data.value = fallbackData;
    slides.value = [...HOME_HERO_FALLBACK_SLIDES];
  }
});

onUnmounted(() => {
  if (slideTimer) window.clearInterval(slideTimer);
});
</script>

<style scoped>
.hero-copy {
  opacity: 0;
  animation: hero-copy-enter 720ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.hero-copy--title {
  animation-delay: 80ms;
}

.hero-copy--subtitle {
  animation-delay: 210ms;
}

@keyframes hero-copy-enter {
  from {
    opacity: 0;
    transform: translateY(18px);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.social-link {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 260ms ease, background-color 260ms ease, border-color 260ms ease;
}

.social-link:hover {
  transform: translateY(-5px) scale(1.1) rotate(4deg);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.3);
}

.social-link:hover :deep(img) {
  animation: social-icon-bounce 500ms ease;
}

.social-link:active {
  transform: translateY(-1px) scale(0.95);
}

.social-link:focus-visible {
  outline: 2px solid white;
  outline-offset: 3px;
}

@keyframes social-icon-bounce {
  0%,
  100% {
    transform: translateY(0) rotate(0);
  }
  45% {
    transform: translateY(-3px) rotate(-8deg);
  }
  70% {
    transform: translateY(1px) rotate(4deg);
  }
}

.hero-cta {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  opacity: 0;
  animation: cta-enter 600ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
  animation-delay: var(--cta-delay, 0ms);
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 220ms ease, filter 220ms ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.hero-cta__label {
  position: relative;
  z-index: 2;
}

.hero-cta__arrow {
  position: relative;
  z-index: 2;
  display: inline-block;
  width: 0;
  margin-left: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateX(-7px);
  transition: width 260ms ease, margin-left 260ms ease, opacity 200ms ease,
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-cta:hover {
  transform: translateY(-2px);
  filter: brightness(1.05);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.16);
}

.hero-cta:hover .hero-cta__arrow {
  width: 1.15em;
  margin-left: 0.45rem;
  opacity: 1;
  transform: translateX(0);
}

.hero-cta:active {
  transform: translateY(0) scale(0.96);
}

.hero-cta:focus-visible {
  outline: 2px solid white;
  outline-offset: 3px;
}

@keyframes cta-enter {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-copy {
    opacity: 1;
    animation: none;
  }

  .social-link,
  .social-link:hover {
    transform: none;
    transition: none;
  }

  .social-link:hover :deep(img) {
    animation: none;
  }

  .hero-cta {
    opacity: 1;
    animation: none;
    transition: none;
  }

  .hero-cta__arrow {
    transition: none;
  }
}
</style>
